<template>
  <view v-if="visible" class="reward-mask" @click="onMaskClick">
    <view class="reward-panel-wrap" :style="wrapStyle">
    <view class="reward-panel" :class="{ 'panel-in': animated }" @click.stop>
      <!-- 弹窗底图：含「恭喜获得」标题与边框 -->
      <image
        src="/static/images/claim-popup/5.png"
        class="panel-bg"
        mode="widthFix"
      />

      <view class="panel-inner">
        <text class="panel-title">{{ title }}</text>
        <!-- 魔法阵光效 -->
        <view class="showcase">
          <image
            src="/static/images/claim-popup/4.png"
            class="portal-img"
            :class="{ 'portal-glow': animated }"
            mode="aspectFit"
          />
          <view class="card-wrap" :class="{ 'card-pop': animated }" :key="currentIndex">
            <image
              v-if="currentItem?.cardImg"
              :src="currentItem.cardImg"
              class="card-img"
              mode="aspectFit"
            />
            <image
              v-else
              src="/static/images/claim-popup/0.png"
              class="card-img card-placeholder"
              mode="aspectFit"
            />
          </view>
        </view>

        <text class="reward-name">{{ currentItem?.cardName || '—' }}</text>
        <text v-if="currentItem?.cardImg" class="reward-count">x{{ currentItem?.count ?? 1 }}</text>

        <text v-if="items.length > 1" class="reward-page">{{ currentIndex + 1 }} / {{ items.length }}</text>

        <view class="confirm-wrap" @click.stop="onConfirm">
          <image
            src="/static/images/claim-popup/3.png"
            class="confirm-bg"
            mode="widthFix"
          />
        </view>
      </view>

      <!-- 多结果左右切换 -->
      <view
        v-if="items.length > 1"
        class="nav-btn nav-prev"
        :class="{ disabled: currentIndex <= 0 }"
        @click.stop="prev"
      >
        <text class="nav-icon">‹</text>
      </view>
      <view
        v-if="items.length > 1"
        class="nav-btn nav-next"
        :class="{ disabled: currentIndex >= items.length - 1 }"
        @click.stop="next"
      >
        <text class="nav-icon">›</text>
      </view>
    </view>
    </view>
  </view>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

const PANEL_WIDTH_RPX = 672
const PANEL_ASPECT = 3248 / 1500

const props = defineProps({
  visible: { type: Boolean, default: false },
  items: { type: Array, default: () => [] },
  title: { type: String, default: '恭喜获得' },
  confirmText: { type: String, default: '确认领取' },
  closeOnMask: { type: Boolean, default: false },
})

const emit = defineEmits(['update:visible', 'confirm', 'close'])

const currentIndex = ref(0)
const animated = ref(false)
const fitScale = ref(1)

const currentItem = computed(() => props.items[currentIndex.value] || null)

const wrapStyle = computed(() => ({
  transform: `scale(${fitScale.value})`,
}))

function updateFitScale() {
  try {
    const sys = uni.getSystemInfoSync()
    const padding = uni.upx2px(64)
    const maxW = sys.windowWidth - padding
    const maxH = sys.windowHeight * 0.9 - padding
    const naturalW = uni.upx2px(PANEL_WIDTH_RPX)
    const naturalH = naturalW * PANEL_ASPECT
    fitScale.value = Math.min(1, maxW / naturalW, maxH / naturalH)
  } catch {
    fitScale.value = 1
  }
}

watch(
  () => props.visible,
  (v) => {
    if (v) {
      updateFitScale()
      currentIndex.value = 0
      animated.value = false
      setTimeout(() => {
        animated.value = true
      }, 80)
    } else {
      animated.value = false
    }
  }
)

watch(
  () => props.items,
  () => {
    if (currentIndex.value >= props.items.length) {
      currentIndex.value = 0
    }
  }
)

function prev() {
  if (currentIndex.value > 0) {
    animated.value = false
    currentIndex.value -= 1
    setTimeout(() => {
      animated.value = true
    }, 40)
  }
}

function next() {
  if (currentIndex.value < props.items.length - 1) {
    animated.value = false
    currentIndex.value += 1
    setTimeout(() => {
      animated.value = true
    }, 40)
  }
}

function close() {
  emit('update:visible', false)
  emit('close')
}

function onConfirm() {
  emit('confirm')
  close()
}

function onMaskClick() {
  if (props.closeOnMask) close()
}
</script>

<style lang="scss" scoped>
.reward-mask {
  position: fixed;
  inset: 0;
  z-index: 900;
  background: rgba(0, 0, 0, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32rpx;
  box-sizing: border-box;
  overflow: hidden;
  animation: mask-in 0.3s ease;
}

.reward-panel-wrap {
  transform-origin: center center;
  flex-shrink: 0;
}

@keyframes mask-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

.reward-panel {
  position: relative;
  width: 672rpx;
  max-width: 100%;
  transform: scale(0.9);
  opacity: 0;
  transition: transform 0.38s cubic-bezier(0.34, 1.35, 0.64, 1), opacity 0.3s ease;
}

.panel-in {
  transform: scale(1);
  opacity: 1;
}

.panel-bg {
  width: 672rpx;
  display: block;
  pointer-events: none;
}

.panel-inner {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  padding-top: 14%;
  padding-bottom: 4%;
}

.panel-title {
  position: absolute;
  top: 7.5%;
  left: 0;
  width: 100%;
  text-align: center;
  font-family: 'HarmonyOS Sans SC', -apple-system, sans-serif;
  font-size: 36rpx;
  font-weight: 600;
  color: #ffffff;
  letter-spacing: 2rpx;
  text-shadow: 0 2rpx 12rpx rgba(138, 43, 226, 0.55);
  z-index: 3;
  pointer-events: none;
}

.showcase {
  position: relative;
  width: 62%;
  aspect-ratio: 1;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1%;
}

.portal-img {
  position: absolute;
  width: 96%;
  height: 96%;
  opacity: 0.95;
  pointer-events: none;
}

.portal-glow {
  animation: portal-glow 2.4s ease-in-out infinite;
}

@keyframes portal-glow {
  0%, 100% { opacity: 0.88; filter: brightness(1); }
  50% { opacity: 1; filter: brightness(1.08); }
}

.card-wrap {
  position: relative;
  z-index: 2;
  width: 70%;
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: scale(0.5);
  opacity: 0;
  transition: transform 0.5s cubic-bezier(0.34, 1.55, 0.64, 1), opacity 0.38s ease;
}

.card-pop {
  transform: scale(1);
  opacity: 1;
}

.card-img {
  width: 100%;
  height: 100%;
  border-radius: 20rpx;
  display: block;
}

.card-placeholder {
  opacity: 0.95;
}

/* 名称 / 数量：30rpx 白字居中 */
.reward-name {
  font-family: 'HarmonyOS Sans SC', -apple-system, sans-serif;
  font-size: 30rpx;
  font-weight: 400;
  color: #ffffff;
  text-align: center;
  line-height: 1.3;
  max-width: 520rpx;
  margin-top: 4rpx;
}

.reward-count {
  font-family: 'HarmonyOS Sans SC', -apple-system, sans-serif;
  font-size: 30rpx;
  font-weight: 400;
  color: #ffffff;
  text-align: center;
  margin-top: 12rpx;
}

.reward-page {
  margin-top: 8rpx;
  font-size: 22rpx;
  color: rgba(141, 143, 192, 0.95);
}

.confirm-wrap {
  margin-top: auto;
  width: 52%;
  flex-shrink: 0;
}

.confirm-bg {
  width: 100%;
  display: block;
}

/* 左右切换：浮在弹窗两侧 */
.nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 56rpx;
  height: 56rpx;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  border: 1rpx solid rgba(255, 255, 255, 0.22);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
}

.nav-btn.disabled {
  opacity: 0.28;
  pointer-events: none;
}

.nav-prev {
  left: 12rpx;
}

.nav-next {
  right: 12rpx;
}

.nav-icon {
  font-size: 36rpx;
  color: #ffffff;
  line-height: 1;
  margin-top: -2rpx;
}
</style>
